import type { Locale } from "./index";

/** Formats numbers with locale-appropriate grouping. Arabic keeps Western digits by default (UAE government convention). */
export function formatNumber(value: number, locale: Locale, opts: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-AE-u-nu-latn" : "en-AE", opts).format(value);
}

export function formatAed(value: number, locale: Locale, fractionDigits = 0): string {
  const n = formatNumber(value, locale, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
  return locale === "ar" ? `${n} درهم` : `AED ${n}`;
}

/** Large amounts in words of scale: "180.7 مليار درهم" / "AED 180.7 bn". */
export function formatAedCompact(value: number | null | undefined, locale: Locale, fractionDigits = 1): string {
  if (value == null) return "—";
  const [scaled, unitAr, unitEn] = Math.abs(value) >= 1_000_000_000 ? [value / 1_000_000_000, "مليار ", " bn"] : Math.abs(value) >= 1_000_000 ? [value / 1_000_000, "مليون ", " m"] : [value, "", ""];
  const n = formatNumber(scaled, locale, { maximumFractionDigits: Math.abs(value) >= 1_000_000_000 ? fractionDigits : 0 });
  return locale === "ar" ? `${n} ${unitAr}درهم` : `AED ${n}${unitEn}`;
}

export function formatPercent(value: number, locale: Locale, fractionDigits = 1): string {
  return `${formatNumber(value, locale, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}%`;
}

// ------------------------------------------------------------------ figures read against a mark (condition 2.9)
//
// One rule for every screen of the platform, the same as the server's FigureFormat: a figure is printed at its usual precision unless that
// rounding would put it on, or across, a mark it is read against – a legal threshold or a target – and then it is printed in full. 40.04% is
// never "40.0%" beside the 40% threshold, AED 300,000,001 never "AED 300 m" beside the AED 300 million threshold, and 94.96% of clocks on
// time never "95%" beside a 95% target. Tests are always run on the unrounded value; this rule only decides how it is printed.

/** The marks a figure is read against: one threshold or target, several, or none. */
export type Marks = number | null | undefined | ReadonlyArray<number | null | undefined>;

const marksOf = (marks: Marks): number[] => (Array.isArray(marks) ? marks : [marks]).filter((m): m is number => typeof m === "number" && Number.isFinite(m));

/** -1 below, 0 on, +1 above – "above" in the law means strictly above. */
const side = (v: number, mark: number) => (v > mark ? 1 : v === mark ? 0 : -1);

const roundTo = (value: number, digits: number) => {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
};

/** True when printing `rounded` for `value` would hide which side of a mark the figure stands on. */
export function hidesMark(value: number, rounded: number, marks: Marks): boolean {
  return rounded !== value && marksOf(marks).some((m) => side(rounded, m) !== side(value, m));
}

/**
 * The fraction digits to print: `digits` where that keeps the figure on its own side of every mark, otherwise the fewest
 * digits that do (40.04 against 40 → 2, 40.0001 → 4), so the figure is shown in full exactly where rounding would mislead.
 */
export function digitsNear(value: number, marks: Marks, digits = 1): number {
  let d = digits;
  while (d < 10 && hidesMark(value, roundTo(value, d), marks)) d += 1;
  return d;
}

/** A number at its usual precision, or in full near a mark. */
export function formatNumberNear(value: number, locale: Locale, marks: Marks, digits = 1): string {
  return formatNumber(value, locale, { maximumFractionDigits: digitsNear(value, marks, digits) });
}

/** A percentage at one decimal (or `digits`), or in full where rounding would put it on or across a mark. */
export function formatPercentNear(value: number, locale: Locale, marks: Marks, digits = 1): string {
  return `${formatNumberNear(value, locale, marks, digits)}%`;
}

/**
 * An amount in words of scale ("AED 450 m"), or in full ("AED 300,000,001") where the scaled figure would put it on or across a mark – the
 * AED 300 million threshold for a turnover or a sales figure.
 */
export function formatAedNear(value: number | null | undefined, locale: Locale, marks: Marks, fractionDigits = 1): string {
  if (value == null) return "—";
  const abs = Math.abs(value);
  const scale = abs >= 1_000_000_000 ? 1_000_000_000 : abs >= 1_000_000 ? 1_000_000 : 1;
  const shown = roundTo(value / scale, scale === 1_000_000_000 ? fractionDigits : 0) * scale;
  if (!hidesMark(value, shown, marks)) return formatAedCompact(value, locale, fractionDigits);
  // Whole dirhams, or the fils (at least two decimals) where whole dirhams would still sit on the mark – the server's FigureFormat.Aed rule.
  const d = digitsNear(value, marks, 0);
  return formatAed(value, locale, d === 0 ? 0 : Math.max(d, 2));
}

/**
 * An index (HHI, ΔHHI) or a count of points as a whole number, or with the fewest decimals that keep it on its own side of each mark: 1,999.6
 * beside the 2,000 band, 149.6 beside the 150 mark (the server's FigureFormat.Index).
 */
export function formatIndexNear(value: number, locale: Locale, marks: Marks): string {
  return formatNumberNear(value, locale, marks, 0);
}
